import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, CircularProgress, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import {
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';

import CustomItem from './components/customItem';
import InputArea from './components/inputArea';
import AppBarComponent from './components/appBar';
import MenuPanel from './components/menuPanel';
import ChatMessages from './components/ChatMessages';
import { ChatProvider } from './contexts/ChatContext';
import SignIn from './components/SignIn';
import SignUp from './components/SignUp';
import ConfirmationDialog from './components/ConfirmationDialog';
import PasswordPrompt from './components/PasswordPrompt';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import AttachmentPanel from './components/AttachmentPanel';
import RegistrationSuccess from './components/RegistrationSuccess';
import { AuthContext } from './contexts/AuthContext';
import { useUserSettings } from './contexts/UserSettingsContext';
import { useMessageHandler } from './contexts/MessageContext';
import { processFile } from './utils/fileUtils'
import { MODEL_METADATA, DEFAULT_ENGINE_MODELS } from './constants/appConstants';
import api from './api/api';
import i18n from './i18n';

const App = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [controlHeight, setControlHeight] = useState(window.innerHeight * 0.2);
  const [headerHeight] = useState(60);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [startY, setStartY] = useState(null);
  const [startHeight, setStartHeight] = useState(null);
  const [selectedService, setSelectedService] = useState('');
  const [showSignIn, setShowSignIn] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [authLoading, setAuthLoading] = useState(true);
  const [codeVerified, setCodeVerified] = useState(false);
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  const [isProcessingReset, setIsProcessingReset] = useState(false);
  const [inputAreaState, setInputAreaState] = useState('normal'); // 'normal', 'hidden', 'maximized'
  const [autoOptimizePrompt, setAutoOptimizePrompt] = useState(false);
  const [enableThinking, setEnableThinking] = useState(false);
  const { t, i18n } = useTranslation();

  const {
    isAuthenticated,
    setIsAuthenticated,
    setUserType,
    userEmail,
    setUserEmail
  } = useContext(AuthContext);

  const { settings, updateSettings, loading: settingsLoading } = useUserSettings();
  const { translit, voiceControl, selectedEngine, fontSize, fontFamily, language, alwaysUseThinking } = settings;

  // Get the current model for the selected engine
  const currentModel = settings?.engineSettings?.[selectedEngine]?.model || DEFAULT_ENGINE_MODELS[selectedEngine];
  
  // Check if current model supports reasoning
  const supportsReasoning = MODEL_METADATA[currentModel]?.reasoning || false;

  // Determine the effective thinking mode state
  const effectiveThinkingEnabled = alwaysUseThinking ? supportsReasoning : enableThinking;

  // Automatically disable thinking mode when switching to a model that doesn't support reasoning
  useEffect(() => {
    if (!supportsReasoning && enableThinking) {
      setEnableThinking(false);
    }
  }, [supportsReasoning, enableThinking]);

  const handleInputAreaStateChange = () => {
    setInputAreaState(currentState => {
      switch (currentState) {
        case 'normal':
          return 'hidden';
        case 'hidden':
          return 'normal';
        case 'maximized':
          return 'normal';
        default:
          return 'normal';
      }
    });
  };

  const handleInputAreaMaximize = () => {
    setInputAreaState(currentState => {
      switch (currentState) {
        case 'normal':
          return 'maximized';
        case 'hidden':
          return 'normal';
        case 'maximized':
          return 'normal';
        default:
          return 'normal';
      }
    });
  };

  const handleMessageSent = () => {
    // Switch from maximized to normal state after message is sent
    if (inputAreaState === 'maximized') {
      setInputAreaState('normal');
    }
  };

  const handleSystemPromptChange = (promptName) => {
    // This is now handled directly by AppBar using MessageContext
    // No need for local state management here
  };

  // Get the message context
  const {
    messageContext,
    isProcessing,
    isRecording,
    isPlaybackActive,
    selectedSystemPrompt,
  } = useMessageHandler();

  const isDisabled = isProcessing || isRecording || isPlaybackActive;

  const handleResetParams = () => {
    try {
      const queryParams = new URLSearchParams(window.location.search);
      const encodedData = queryParams.get('data');

      if (encodedData) {
        // Use browser's built-in atob for base64 decoding
        const decodedParams = atob(encodedData);
        const params = new URLSearchParams(decodedParams);

        const token = params.get('token');
        const email = params.get('email');

        if (token && email) {
          setIsProcessingReset(true);
          setResetToken(token);
          setResetEmail(decodeURIComponent(email));
          setShowResetPassword(true);
          setCodeVerified(true);
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    } catch (error) {
      console.error('Error processing reset parameters:', error);
    } finally {
      setIsProcessingReset(false);
    }
  };

  useEffect(() => {
    handleResetParams();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
      setIsAuthenticated(true);
    }
  }, [setIsAuthenticated]);

  // Effect for initial actions
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Remove the isAuthenticated condition - we want to check regardless
        const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/current-user`);
        setIsAuthenticated(response.data.isAuthenticated);
        if (response.data.isAuthenticated) {
          setUserEmail(response.data.user.email);
          setUserType(response.data.userType);
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        setIsAuthenticated(false);
        setUserEmail('');
        setUserType(null);
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();
  }, [setIsAuthenticated, setUserEmail, setUserType]);

  // In App.js or your root component
  useEffect(() => {
    const handleLanguageChange = async () => {
      const userSettings = localStorage.getItem('userSettings');
      if (userSettings) {
        const { language } = JSON.parse(userSettings);
        if (language) {
          await i18n.changeLanguage(language);
        }
      }
    };

    handleLanguageChange();
  }, []);

  const handleUpload = async (files) => {
    try {
      const newAttachments = await Promise.all(
        files.map(file => processFile(file))
      );
      setAttachments(prev => [...prev, ...newAttachments]);
    } catch (error) {
      console.error('Error processing files:', error);
      // Handle error (show notification, etc.)
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachments(attachments => {
      const newAttachments = [...attachments];
      URL.revokeObjectURL(newAttachments[index].preview);
      newAttachments.splice(index, 1);
      return newAttachments;
    });
  };

  // Handler for mouse down event on resizer
  const handleMouseDown = (e) => {
    setIsResizing(true);
    setStartY(e.clientY);
    setStartHeight(controlHeight);
  };

  // Handler for mouse move event during resizing
  const handleMouseMove = (e) => {
    if (isResizing) {
      const newHeight = startHeight - (e.clientY - startY);
      setControlHeight(newHeight > 150 ? newHeight : 150);
    }
  };

  // Handler for mouse up event to stop resizing
  const handleMouseUp = () => {
    setIsResizing(false);
  };

  // Effect for adding and removing event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleMenuOpen = () => {
    setMenuOpen(true);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  // Handlers for sign in, sign up, and logout actions
  const handleSignInClick = () => {
    setShowSignIn(true);
  };

  const handleSignUpClick = () => {
    setShowSignIn(false);
    setShowSignUp(true);
  };

  const handleCancel = () => {
    setShowSignIn(false);
    setShowSignUp(false);
    setShowForgotPassword(false);
  };

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      // Capture the current app version before clearing storage
      const currentVersion = localStorage.getItem('appVersion');

      // Continue with logout
      await api.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/logout`);

      // Clear JWT and auth headers
      delete api.defaults.headers.common['Authorization'];

      // Clear authentication state
      setIsAuthenticated(false);
      setUserEmail('');
      setUserType(null);

      // Instead of clearing all of localStorage, only remove the authentication token
      // and any sensitive data, but KEEP the version information
      localStorage.removeItem('jwtToken');
      localStorage.removeItem('userInfo');

      // Keep the app version to prevent reload
      if (currentVersion) {
        localStorage.setItem('appVersion', currentVersion);
      }

      // Close the dialog
      setShowLogoutDialog(false);

    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      setShowLogoutDialog(false);
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutDialog(false);
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setShowSignIn(false);

    // Fetch the user email after successful login
    const fetchUserEmail = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/current-user`);
        if (response.data.isAuthenticated) {
          setUserEmail(response.data.user.email);
          setUserType('registered');
        }
      } catch (error) {
        console.error('Error fetching user email:', error);
      }
    };
    fetchUserEmail();
  };

  const handleSignUpSuccess = () => {
    setShowSignUp(false);
    setShowRegistrationSuccess(true);
  }

  const handleForgotPassword = () => {
    setShowSignIn(false);
    setShowForgotPassword(true);
  };

  const handleCloseResetPassword = () => {
    setShowResetPassword(false);
    setResetToken('');
    setResetEmail('');
    setShowSignIn(true);
  };

  const handleTranslitChange = async (event) => {
    const newTranslit = event.target.checked;
    await updateSettings({ translit: newTranslit });
  };

  const handleVoiceControlChange = async (event) => {
    const newVoiceControl = event.target.checked;
    await updateSettings({ voiceControl: newVoiceControl });
  };

  const handleEngineChange = async (newEngine) => {
    await updateSettings({ selectedEngine: newEngine });
  };

  // Loading state
  if (authLoading || settingsLoading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: theme.palette.background.default
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // Render the application
  return (
    <ChatProvider>
      <Box sx={{
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        flexGrow: 1,
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        fontSize: '0.75rem'
      }}>
        {
          !isAuthenticated ? (
            isProcessingReset ? (
              <CircularProgress />
            ) : showResetPassword ? (
              <ResetPassword
                open={showResetPassword}
                onClose={handleCloseResetPassword}
                resetToken={resetToken}
                resetEmail={resetEmail}
              />
            ) : showRegistrationSuccess ? (
              <RegistrationSuccess />

            ) : codeVerified ? (
              showSignIn ? (
                <SignIn
                  onCancel={handleCancel}
                  onSignUpClick={handleSignUpClick}
                  onLoginSuccess={handleLoginSuccess}
                  onForgotPasswordClick={handleForgotPassword}
                />
              ) : showSignUp ? (
                <SignUp
                  onCancel={handleCancel}
                  onSignInClick={handleSignInClick}
                  onSignUpSuccess={handleSignUpSuccess}
                  language={language}
                />
              ) : showForgotPassword ? (
                <ForgotPassword onCancel={handleCancel} />

              ) : (
                <SignIn
                  onCancel={handleCancel}
                  onSignUpClick={handleSignUpClick}
                  onLoginSuccess={handleLoginSuccess}
                  onForgotPasswordClick={handleForgotPassword}
                />
              )
            ) : (
              <PasswordPrompt onPasswordVerified={() => setCodeVerified(true)} />
            )
          ) : (
            <Grid container sx={{ flexGrow: 1, height: '100%' }}>
              <Grid item xs={menuOpen ? (isMobile ? 1 : 10) : 12} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <CustomItem sx={{ flexShrink: 0, height: `${headerHeight}px`, padding: 0 }}>
                  <AppBarComponent
                    onMenuClick={handleMenuOpen}
                    menuOpen={menuOpen}
                    translit={translit}
                    onTranslitChange={handleTranslitChange}
                    voiceControl={voiceControl}
                    onVoiceControlChange={handleVoiceControlChange}
                    onSystemPromptChange={handleSystemPromptChange}
                  />
                </CustomItem>
                <Divider />
                <CustomItem sx={{ 
                  flexGrow: 1, 
                  overflow: 'hidden',
                  height: inputAreaState === 'maximized' ? '0px' : 'auto',
                  transition: 'height 0.3s ease'
                }}>
                  <ChatMessages
                    translit={translit}
                    fontSize={fontSize}
                    fontFamily={fontFamily}
                    selectedEngine={selectedEngine}
                    selectedService={selectedService}
                    autoOptimizePrompt={autoOptimizePrompt}
                    enableThinking={effectiveThinkingEnabled}
                  />
                </CustomItem>
                <Box sx={{ flexShrink: 0 }}>
                  <AttachmentPanel attachments={attachments} onRemove={handleRemoveAttachment} />
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '4px 16px',
                    backgroundColor: theme.palette.background.appBar,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    flexShrink: 0
                  }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={autoOptimizePrompt}
                            onChange={(e) => setAutoOptimizePrompt(e.target.checked)}
                            size="small"
                            color="primary"
                            disabled={isDisabled}
                          />
                        }
                        label={
                          <Typography>
                            {t('input.autoOptimizePrompt')}
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={effectiveThinkingEnabled}
                            onChange={(e) => setEnableThinking(e.target.checked)}
                            size="small"
                            color="primary"
                            disabled={isDisabled || !supportsReasoning || alwaysUseThinking}
                          />
                        }
                        label={
                          <Typography sx={{ 
                            color: (!supportsReasoning || alwaysUseThinking) ? 'text.disabled' : 'text.primary' 
                          }}>
                            {t('input.enableThinking')}
                          </Typography>
                        }
                        disabled={isDisabled || !supportsReasoning || alwaysUseThinking}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {/* Up arrow - appears in normal and hidden states */}
                      {(inputAreaState === 'normal' || inputAreaState === 'hidden') && (
                        <Tooltip title={inputAreaState === 'normal' ? t('input.maximizeInput') : t('input.showInput')}>
                          <IconButton
                            onClick={inputAreaState === 'normal' ? handleInputAreaMaximize : handleInputAreaStateChange}
                            disabled={isDisabled}
                            size="small"
                            sx={{
                              color: theme.palette.text.secondary,
                              '&:hover': {
                                color: theme.palette.primary.main,
                                backgroundColor: 'transparent'
                              }
                            }}
                          >
                            <KeyboardArrowUp />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Down arrow - appears in normal and maximized states */}
                      {(inputAreaState === 'normal' || inputAreaState === 'maximized') && (
                        <Tooltip title={inputAreaState === 'normal' ? t('input.hideInput') : t('input.normalizeInput')}>
                          <IconButton
                            onClick={handleInputAreaStateChange}
                            disabled={isDisabled}
                            size="small"
                            sx={{
                              color: theme.palette.text.secondary,
                              '&:hover': {
                                color: theme.palette.primary.main,
                                backgroundColor: 'transparent'
                              }
                            }}
                          >
                            <KeyboardArrowDown />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Divider />
                <CustomItem sx={{
                  flexShrink: 0,
                  height: inputAreaState === 'hidden' ? '0px' : 
                         inputAreaState === 'maximized' ? `${window.innerHeight - headerHeight - 80}px` : 
                         `${controlHeight}px`,
                  position: 'relative',
                  minHeight: inputAreaState === 'hidden' ? '0px' : '150px',
                  overflow: 'hidden',
                  transition: 'height 0.3s, min-height 0.3s',
                }}>
                  <div
                    onMouseDown={handleMouseDown}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '10px',
                      cursor: 'row-resize',
                      zIndex: 1,
                      display: (inputAreaState === 'normal') ? 'block' : 'none',
                    }}
                  />
                  <InputArea
                    translit={translit}
                    selectedEngine={selectedEngine}
                    onEngineChange={handleEngineChange}
                    selectedService={selectedService}
                    setSelectedService={setSelectedService}
                    isAuthenticated={isAuthenticated}
                    onUpload={handleUpload}
                    attachments={attachments}
                    setAttachments={setAttachments}
                    isHidden={inputAreaState === 'hidden'}
                    autoOptimizePrompt={autoOptimizePrompt}
                    enableThinking={effectiveThinkingEnabled}
                    onMessageSent={handleMessageSent}
                  />
                </CustomItem>
              </Grid>
              {menuOpen && (
                <Grid
                  item
                  xs={isMobile ? 12 : 2}
                  sx={{
                    padding: 0,
                    ...(isMobile && {
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 1300,
                      width: '100%',
                      height: '100vh'
                    })
                  }}
                >
                  <MenuPanel
                    open={menuOpen}
                    onClose={handleMenuClose}
                    onLogoutClick={handleLogoutClick}
                    userEmail={userEmail}
                    onEngineChange={handleEngineChange}
                  />
                </Grid>
              )}
            </Grid>
          )}
        <ConfirmationDialog
          open={showLogoutDialog}
          titleKey="auth.logout"
          contentKey="auth.logoutConfirmation"
          onClose={handleLogoutCancel}
          onConfirm={handleLogoutConfirm}
        />
      </Box>
    </ChatProvider>
  );
};

export default App;